import React, { useEffect, useRef } from 'react';

/**
 * Drifting embers — a nod to the bonfire pit. Canvas keeps this off the
 * main thread's layout/paint path, unlike dozens of animated DOM nodes.
 */
const EmberField = ({ density = 34, className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    let width = 0;
    let height = 0;
    let raf = 0;
    let embers = [];
    let running = true;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const seed = () => {
      embers = Array.from({ length: density }, () => ({
        x: Math.random() * width,
        y: height + Math.random() * height,
        r: 0.6 + Math.random() * 1.8,
        vy: 0.15 + Math.random() * 0.45,
        drift: (Math.random() - 0.5) * 0.25,
        phase: Math.random() * Math.PI * 2,
        life: 0.3 + Math.random() * 0.7,
      }));
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const draw = () => {
      if (!running) return;
      ctx.clearRect(0, 0, width, height);
      for (const e of embers) {
        e.y -= e.vy;
        e.phase += 0.012;
        e.x += e.drift + Math.sin(e.phase) * 0.22;
        if (e.y < -12) {
          e.y = height + 8;
          e.x = Math.random() * width;
        }
        const twinkle = 0.45 + Math.sin(e.phase * 1.7) * 0.35;
        const alpha = Math.max(0, twinkle * e.life);
        const g = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, e.r * 5);
        g.addColorStop(0, `rgba(255, 186, 110, ${alpha})`);
        g.addColorStop(0.4, `rgba(228, 128, 52, ${alpha * 0.35})`);
        g.addColorStop(1, 'rgba(228, 128, 52, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r * 5, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);

    // Pause when scrolled out of view so we aren't burning frames off-screen.
    const io = new IntersectionObserver(([entry]) => {
      running = entry.isIntersecting;
      if (running) raf = requestAnimationFrame(draw);
      else cancelAnimationFrame(raf);
    });
    io.observe(canvas);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      io.disconnect();
    };
  }, [density]);

  return <canvas ref={canvasRef} className={`ember-field ${className}`} aria-hidden="true" />;
};

export default EmberField;
